import React, { useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getImageUrl } from '../utils/imageUrl';
import { getPasseiosLang } from '../utils/passeioLang';

function InfiniteCarousel({ passeios, title, className = '' }) {
  const { t, i18n } = useTranslation();
  const scrollContainerRef = useRef(null);
  const isScrolling = useRef(false);
  
  // Obter passeios no idioma correto
  const passeiosLang = getPasseiosLang(passeios, i18n.language);

  // Função para avançar 2 cards
  const scrollNext = () => {
    const container = scrollContainerRef.current;
    if (!container) return;
    
    const cardWidth = 320; // w-80 = 320px
    const gap = 24; // gap-6 = 24px
    const cardWithGap = cardWidth + gap;
    const scrollAmount = cardWithGap * 2; // 2 cards
    
    // Desabilitar temporariamente o handleScroll para não interferir
    isScrolling.current = true;
    
    container.scrollBy({
      left: scrollAmount,
      behavior: 'smooth'
    });
    
    // Reabilitar após o scroll completar
    setTimeout(() => {
      isScrolling.current = false;
    }, 800);
  };

  // Função para retroceder 2 cards
  const scrollPrev = () => {
    const container = scrollContainerRef.current;
    if (!container) return;
    
    const cardWidth = 320; // w-80 = 320px
    const gap = 24; // gap-6 = 24px
    const cardWithGap = cardWidth + gap;
    const scrollAmount = cardWithGap * 2; // 2 cards
    
    // Desabilitar temporariamente o handleScroll para não interferir
    isScrolling.current = true;
    
    container.scrollBy({
      left: -scrollAmount,
      behavior: 'smooth'
    });
    
    // Reabilitar após o scroll completar
    setTimeout(() => {
      isScrolling.current = false;
    }, 800);
  };

  // Duplicar os passeios 3 vezes para criar efeito infinito perfeito
  // [cópia1] [cópia2] [cópia3]
  const duplicatedPasseios = passeiosLang && passeiosLang.length > 0 
    ? [...passeiosLang, ...passeiosLang, ...passeiosLang]
    : [];
  
  // Calcular largura de um set de passeios
  const itemWidth = 320; // w-80 = 320px
  const gap = 24; // gap-6 = 24px
  const setWidth = passeiosLang && passeiosLang.length > 0 
    ? (itemWidth + gap) * passeiosLang.length 
    : 0;

  useEffect(() => {
    if (!passeiosLang || passeiosLang.length === 0) return;
    const container = scrollContainerRef.current;
    if (!container) return;

    // Aguardar o layout ser renderizado
    const initScroll = () => {
      // Posicionar no início da segunda cópia (meio) para permitir scroll infinito
      // Garantir que está alinhado ao início de um card
      if (setWidth > 0) {
        const cardWithGap = itemWidth + gap;
        const alignedPosition = Math.floor(setWidth / cardWithGap) * cardWithGap;
        container.scrollLeft = alignedPosition;
      }
    };
    
    // Aguardar um pouco mais para garantir que o layout está pronto
    const timer = setTimeout(initScroll, 200);

    const handleScroll = () => {
      // Ignorar se estiver em scroll programático (pelos botões)
      if (isScrolling.current) return;
      
      requestAnimationFrame(() => {
        const scrollLeft = container.scrollLeft;
        const threshold = 50;
        const cardWithGap = itemWidth + gap;
        
        // Se chegou próximo ao final da terceira cópia, reposiciona instantaneamente para o equivalente na segunda
        if (scrollLeft >= setWidth * 2 - threshold) {
          container.style.scrollBehavior = 'auto';
          const newPosition = scrollLeft - setWidth;
          // Alinhar ao início de um card
          const alignedPosition = Math.floor(newPosition / cardWithGap) * cardWithGap;
          container.scrollLeft = alignedPosition;
          // Restaurar smooth após um frame
          setTimeout(() => {
            container.style.scrollBehavior = 'smooth';
          }, 0);
        }
        // Se voltou para o início da primeira cópia, reposiciona instantaneamente para o equivalente na segunda
        else if (scrollLeft <= threshold) {
          container.style.scrollBehavior = 'auto';
          const newPosition = scrollLeft + setWidth;
          // Alinhar ao início de um card
          const alignedPosition = Math.floor(newPosition / cardWithGap) * cardWithGap;
          container.scrollLeft = alignedPosition;
          // Restaurar smooth após um frame
          setTimeout(() => {
            container.style.scrollBehavior = 'smooth';
          }, 0);
        }
      });
    };

    container.addEventListener('scroll', handleScroll, { passive: true });
    
    return () => {
      clearTimeout(timer);
      container.removeEventListener('scroll', handleScroll);
    };
  }, [setWidth, passeiosLang]);

  if (!passeiosLang || passeiosLang.length === 0) return null;

  return (
    <div className={`mb-16 ${className}`}>
      <h3 className="text-2xl font-medium text-stone-800 mb-6">{title}</h3>
      <div className="relative carousel-container">
        {/* Botão Anterior - Esquerda */}
        <button
          onClick={scrollPrev}
          className="carousel-button absolute left-2 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-stone-800 p-3 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 z-10 hover:scale-125"
          aria-label="Voltar 2 cards"
          title="Voltar 2 cards"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>

        {/* Botão Próximo - Direita */}
        <button
          onClick={scrollNext}
          className="carousel-button absolute right-2 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-stone-800 p-3 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 z-10 hover:scale-125"
          aria-label="Avançar 2 cards"
          title="Avançar 2 cards"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>

        <div 
          ref={scrollContainerRef}
          className="flex gap-6 overflow-x-auto overflow-y-hidden pb-4 scrollbar-hide"
          style={{ 
            WebkitOverflowScrolling: 'touch',
            scrollSnapType: 'x mandatory',
            paddingLeft: '1rem',
            paddingRight: '1rem'
          }}
        >
          {duplicatedPasseios.map((passeio, index) => (
            <div
              key={`${passeio.id}-${index}`}
              className="group bg-white rounded-xl shadow-md overflow-hidden hover:shadow-lg transition-all duration-300 border border-stone-200 flex-shrink-0 w-80 snap-start flex flex-col"
              style={{ scrollSnapAlign: 'start', minHeight: '520px' }}
            >
              <div className="relative h-48 overflow-hidden flex-shrink-0">
                {passeio.Imagens && passeio.Imagens.length > 0 ? (
                  <img
                    src={getImageUrl(passeio.Imagens[0].imagem_url)}
                    alt={passeio.nome}
                    className="w-full h-full object-cover object-center group-hover:scale-110 transition-transform duration-700"
                    onError={(e) => {
                      e.target.src = 'https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?w=400&h=300&fit=crop&q=80';
                    }}
                  />
                ) : passeio.imagem_url ? (
                  <img
                    src={getImageUrl(passeio.imagem_url)}
                    alt={passeio.nome}
                    className="w-full h-full object-cover object-center group-hover:scale-110 transition-transform duration-700"
                    onError={(e) => {
                      e.target.src = 'https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?w=400&h=300&fit=crop&q=80';
                    }}
                  />
                ) : (
                  <img
                    src="https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?w=400&h=300&fit=crop&q=80"
                    alt="Passeio turístico"
                    className="w-full h-full object-cover object-center"
                  />
                )}
                <div className="absolute top-3 right-3">
                  <span className="bg-green-600 text-white px-2 py-1 rounded text-xs font-medium">
                    {passeio.disponivel ? t('tours.available') : t('tours.unavailable')}
                  </span>
                </div>
              </div>
              <div className="p-5 flex flex-col flex-grow">
                <h3 className="text-xl font-semibold text-stone-900 mb-2 line-clamp-2 min-h-[3.5rem]">
                  {passeio.nome}
                </h3>
                <p className="text-stone-600 mb-3 line-clamp-2 text-sm min-h-[2.5rem] flex-grow">
                  {passeio.descricao}
                </p>
                <div className="flex flex-wrap items-center gap-2 mb-4 text-xs text-stone-500 min-h-[2rem]">
                  <span>📍 {passeio.localizacao.length > 20 ? passeio.localizacao.substring(0, 20) + '...' : passeio.localizacao}</span>
                  {passeio.duracao && <span>⏱️ {passeio.duracao}</span>}
                  <span>👥 {t('tours.upTo')} {passeio.capacidade_maxima || 50} {t('tours.people')}</span>
                </div>
                <div className="flex items-center justify-end pt-3 border-t border-stone-200 mt-auto">
                  <Link
                    to={`/passeio/${passeio.id}`}
                    className="bg-stone-800 text-white px-5 py-2 rounded-lg text-sm font-medium hover:bg-stone-700 transition"
                  >
                    {t('tours.viewDetails')}
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default InfiniteCarousel;

